import { Box } from '@mui/material';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import React from 'react';
import { GaugeChart, GaugeChartProps } from 'src/components/BeaconRedesignComponents/GaugeChart/GaugeChart';
import SpiderChart, { SpiderChartProps } from 'src/components/BeaconRedesignComponents/SpiderChart/SpiderChart';
import { SpiderChartLoadingSkeleton } from 'src/components/BeaconRedesignComponents/SpiderChart/SpiderChartLoadingSkeleton';

interface ContentSpiderChartProps {
  /**
   * The text that appears in the center of gauge chart. Defaults to empty string.
   */
  gaugeText?: string;
  /**
   * The value that determines how the gauge chart is filled.
   */
  gaugeValue: number;
  /**
   * The color of the gauge chart color.
   * If not provided, it will default to green if the gauge value is greater than 70, red if less than 50, and gold otherwise.
   */
  gaugeColor?: string;
  /**
   * Any additional props to pass to the gauge chart found.
   */
  gaugeChartProps?: Partial<GaugeChartProps> | undefined;

  /**
   * Additional spider chart props to pass to the spider chart.
   */
  spiderChartProps?: Partial<SpiderChartProps>;

  /**
   * Primary spider chart data
   */
  primarySpiderData: SpiderChartProps['primaryData'];
  /**
   * Secondary spider chart data
   */
  secondarySpiderData?: SpiderChartProps['comparisonData'];

  /**
   * Categories for the spider chart
   */
  categories: SpiderChartProps['categories'];

  loading?: boolean;
}

/**
 * Combines the radar and the gauge chart to create a generic spider chart with some default behavior.
 */
export const ContentSpiderChart = ({
  gaugeValue,
  gaugeText,
  gaugeColor,
  gaugeChartProps,
  primarySpiderData,
  secondarySpiderData,
  categories,
  spiderChartProps,
  loading
}: ContentSpiderChartProps) => {
  const theme = useStacklineTheme();

  const defaultGaugeColor =
    gaugeValue >= 0.7 ? theme.colors.success : gaugeValue < 0.5 ? theme.colors.error : theme.colors.accentGold;

  return (
    <>
      {loading ? (
        <div
          style={{ height: '448px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
        >
          <SpiderChartLoadingSkeleton />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '448px',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              background: 'white',
              borderRadius: '999px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0.5px',
              zIndex: 1
            }}
          >
            <GaugeChart
              chartOptions={{
                chart: {
                  height: 96,
                  width: 96
                },
                pane: {
                  background: [
                    {
                      backgroundColor: gaugeColor || defaultGaugeColor,
                      innerRadius: '0%',
                      outerRadius: '117%'
                    },
                    {
                      innerRadius: '0%',
                      outerRadius: '0%'
                    }
                  ]
                },
                series: [
                  {
                    type: 'solidgauge',
                    data: [
                      {
                        y: gaugeValue,
                        color: 'white',
                        radius: `106%`,
                        innerRadius: `97%`
                      }
                    ]
                  }
                ],
                title: {
                  text: gaugeText,
                  style: {
                    color: 'white',
                    fontFamily: 'Roboto',
                    fontSize: '20px',
                    fontWeight: '700',
                    lineHeight: '28px',
                    letterSpacing: '0.08px',
                    textAlign: 'center'
                  }
                },
                yAxis: {
                  min: 0,
                  max: 1
                }
              }}
              {...(gaugeChartProps ?? {})}
            />
          </Box>
          <div
            style={{
              position: 'relative',
              width: '448px',
              height: '448px'
            }}
          >
            <SpiderChart
              primaryData={primarySpiderData}
              comparisonData={secondarySpiderData}
              categories={categories}
              chartWidth={448}
              chartHeight={448}
              getChartOptions={(defaultOptions) => {
                const [comparisonSeries, primarySeries] = defaultOptions.series as Highcharts.SeriesAreasplineOptions[];

                // Configure primary series (green)
                primarySeries.color = defaultGaugeColor;
                primarySeries.fillColor = hexToRgba(defaultGaugeColor, 0.15);
                primarySeries.marker = {
                  ...primarySeries.marker,
                  // outer ring color
                  lineColor: hexToRgba(defaultGaugeColor, 0.2),
                  // inner dot color
                  fillColor: defaultGaugeColor,
                  width: 10,
                  height: 10,
                  lineWidth: 4,
                  radius: 2
                };

                // Configure comparison series (gray) to match
                if (comparisonSeries && secondarySpiderData) {
                  comparisonSeries.marker = {
                    ...comparisonSeries.marker,
                    lineColor: hexToRgba(theme.colors.casper, 0.2),
                    fillColor: theme.colors.casper,
                    width: 10,
                    height: 10,
                    lineWidth: 4,
                    radius: 2
                  };
                }

                const newOptions = {
                  ...defaultOptions,
                  series: [...defaultOptions.series, primarySeries],
                  chart: {
                    ...defaultOptions.chart,
                    animation: false,
                    events: {
                      ...defaultOptions.chart?.events,
                      render() {
                        // Force consistent positioning of labels after render
                        const xAxis = this.xAxis[0];
                        if (xAxis && xAxis.labelGroup) {
                          xAxis.labelGroup.attr({ translateX: 0, translateY: 0 });
                        }
                      }
                    }
                  },
                  plotOptions: {
                    ...defaultOptions.plotOptions,
                    series: {
                      ...defaultOptions.plotOptions?.series,
                      animation: false
                    }
                  },
                  xAxis: {
                    ...defaultOptions.xAxis,
                    labels: {
                      ...defaultOptions.xAxis?.labels,
                      animation: false
                    }
                  }
                };
                return newOptions;
              }}
              {...(spiderChartProps ?? {})}
            />
          </div>
        </div>
      )}
    </>
  );
};
